import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Clock, MapPin, Phone, ArrowRight } from 'lucide-react';
import { useSmartContent } from '../context/SmartSiteContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEAM_PHOTOS = {
  gayle: 'https://shopkyis.com/wp-content/uploads/2024/10/DSC_1917b.webp',
  danielle: 'https://shopkyis.com/wp-content/uploads/2024/10/Danielle-JOhnson-Headshot.webp',
  gabrielle: 'https://shopkyis.com/wp-content/uploads/2024/10/Gabrielle-McLeod-Headshot.webp',
};

function useReveal(threshold = 0.12, deps = []) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    [el, ...el.querySelectorAll('.reveal,.reveal-left,.reveal-right,.stagger-grid')].forEach((t) => obs.observe(t));
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threshold, ...deps]);
  return ref;
}

const AboutPage = () => {
  const [team, setTeam] = useState([]);
  const heroData = useSmartContent('about-hero', 'hero');
  const h = heroData.items?.[0]?.data || {};
  const historyData=useSmartContent('about-history','section_copy');
  const history=historyData.items?.[0]?.data || {};
  const presentData=useSmartContent('about-present','section_copy');
  const present=presentData.items?.[0]?.data || {};
  const actionData=useSmartContent('about-cta','cta');
  const action=actionData.items?.[0]?.data || {};
  const historyRef = useReveal(0.1);
  const presentRef = useReveal(0.1);
  const teamRef = useReveal(0.1, [team.length]);
  const contactRef = useReveal(0.15);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/team`).then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  const getTeamPhoto = (name) => {
    const n = name.toLowerCase();
    if (n.includes('gayle')) return TEAM_PHOTOS.gayle;
    if (n.includes('danielle')) return TEAM_PHOTOS.danielle;
    if (n.includes('gabrielle')) return TEAM_PHOTOS.gabrielle;
    return null;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Hero */}
      <section className="relative py-24 bg-[#1B3A5C] overflow-hidden">
        <div className="absolute inset-0 opacity-5 pattern-dots" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <nav className="text-sm text-blue-200/50 mb-4">
            <Link to="/" className="hover:text-[#F5A623]">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-white">About</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4 text-white">{h.heading || 'About Us'}</h1>
          <p className="text-blue-100/70 max-w-2xl">
            {h.body || 'Bringing your ideas to life requires us to test the limits of our Kre8ivity every day.'}
          </p>
          {h.image && <img src={h.image} alt="" className="mt-6 max-h-64 max-w-full object-contain rounded-lg"/>}
          {h.cta_text && <Link to={h.cta_link || '/shop'} className="inline-block mt-5 px-5 py-3 bg-[#F5A623] text-gray-900 rounded-full">{h.cta_text}</Link>}
        </div>
      </section>

      {/* History */}
      <section ref={historyRef} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="reveal-left">
              <p className="text-[#4A90A4] text-sm font-semibold tracking-[0.2em] uppercase mb-4">Our History</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6 text-gray-900">
                {history.heading || 'Kre8ing Your IdeaS Family Owned and Operated'}
              </h2>
              <p className="text-[#F5A623] text-sm font-medium mb-6">(Formerly) Keeping You In Stitches est.1996</p>
              <div className="space-y-5 text-gray-600 leading-relaxed">
                {history.body ? <p className="whitespace-pre-line">{history.body}</p> : <>                <p>In 1996, Gayle Johnson found herself in need of an embroidery service that no one seemed to offer in her community. After purchasing an embroidery machine and enrolling in a one-day training course, Gayle started Keeping You In Stitches.</p>
                <p>In true entrepreneurial spirit, Gayle worked tirelessly to build her dreams, while working full time at International Paper from 12am-8am. Shortly after, she was given the opportunity to set up a kiosk in Security Square Mall.</p>
                <p>Kre8ing Your IdeaS continued to grow, expanding to Owings Mills Mall in 2002, to its own storefront location on Security Blvd in 2008, and in 2021 we purchased our very own building.</p></>}
              </div>
            </div>
            <div className="reveal-right relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={history.image || "https://customer-assets.emergentagent.com/job_3ae07d98-14ea-4fac-95c0-7c7c943bfaa7/artifacts/dujzaeeh_About%20us%20page.png"}
                  alt="KYIS History"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 p-6 glass rounded-2xl shadow-xl max-w-xs">
                <p className="text-gray-700 italic text-sm leading-relaxed mb-2">
                  "A woman whose vision to provide a service to her community has blossomed into a nationally recognized brand and a flourishing legacy."
                </p>
                <p className="text-[#F5A623] font-bold text-sm">— Gayle Johnson</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Present Day */}
      <section ref={presentRef} className="py-24 bg-[#4A90A4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal">
            <p className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4">Why Choose Us</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6 text-white">{present.heading || 'Our Present'}</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="reveal-left text-white/80 leading-relaxed space-y-6">
              {present.body ? <p className="whitespace-pre-line">{present.body}</p> : <>              <p>The business has blossomed into a family enterprise as Gayle has enlisted the assistance of her daughters Danielle, who specializes in designs and graphics, and Gabrielle who manages customer service and operations.</p>
              <p>Having successfully navigated a global pandemic, we continue to deliver quality embroidery services at reasonable cost. In 2021 we purchased our very own building and are excited to grow and expand the business.</p></>}
            </div>
            <div className="reveal-right p-8 bg-white rounded-2xl shadow-lg">
              <h3 className="text-xl font-bold text-[#F5A623] mb-4">{action.heading || 'We specialize in you!'}</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                {action.body || 'Bringing your ideas to life requires us to test the limits of our Kre8ivity every day. If you can dream it, we will try it!'}
              </p>
              <Link
                to={action.cta_link || "/contact"}
                className="inline-flex items-center px-6 py-3 bg-[#F5A623] text-gray-900 font-bold rounded-full btn-glow text-sm"
              >
                {action.cta_text || 'Make Appointment'} <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      {team.length > 0 && (
        <section ref={teamRef} className="py-24 bg-[#1B3A5C]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <p className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4">Our Team</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4 text-white">The KYIS Dream Team</h2>
              <p className="text-blue-100/60 max-w-2xl mx-auto">From beginning to end, we've got you covered.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 stagger-grid reveal">
              {team.map((member, i) => (
                <div key={member.id} className="team-card text-center bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10" data-testid={`about-team-${i}`}>
                  <div className="team-photo w-44 h-44 mx-auto mb-6 rounded-full overflow-hidden border-4 border-[#F5A623]/40">
                    {getTeamPhoto(member.name) ? (
                      <img src={getTeamPhoto(member.name)} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#F5A623]/30 to-[#4A90A4]/30 flex items-center justify-center">
                        <span className="text-4xl font-bold text-white">{member.name.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-xl font-bold mb-1 text-white">{member.name}</h3>
                  <p className="text-[#F5A623] text-sm font-semibold mb-1">{member.nickname}</p>
                  <p className="text-blue-200/70 text-sm mb-4">{member.title}</p>
                  {member.bio && <p className="text-sm text-blue-100/50 leading-relaxed">{member.bio}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Info */}
      <section ref={contactRef} className="py-20 bg-[#FDF6E9]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 stagger-grid reveal">
            <div className="text-center p-6 bg-white rounded-2xl shadow-sm">
              <MapPin className="w-8 h-8 text-[#4A90A4] mx-auto mb-4" />
              <h3 className="font-bold mb-2 text-gray-900">Location</h3>
              <p className="text-gray-500 text-sm">129 Slade Ave<br />Pikesville, MD 21208</p>
            </div>
            <div className="text-center p-6 bg-white rounded-2xl shadow-sm">
              <Phone className="w-8 h-8 text-[#4A90A4] mx-auto mb-4" />
              <h3 className="font-bold mb-2 text-gray-900">Phone</h3>
              <a href="tel:443-940-6660" className="text-gray-500 text-sm hover:text-[#4A90A4] transition-colors">443-940-6660</a>
            </div>
            <div className="text-center p-6 bg-white rounded-2xl shadow-sm">
              <Clock className="w-8 h-8 text-[#4A90A4] mx-auto mb-4" />
              <h3 className="font-bold mb-2 text-gray-900">Hours</h3>
              <p className="text-gray-500 text-sm">Mon - Fri: 10am - 6pm<br />Sat - Sun: Closed</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
